// ============================================================================
// PhishingIOCSyncPanel.js
// Drop into src/components/
//
// Shows how many phishing URLs are synced to the IOC database,
// and lets Super Admins trigger a sync.
//
// Usage in PhishingFeedView.js:
//   import PhishingIOCSyncPanel from './PhishingIOCSyncPanel';
//   ...
//   <PhishingIOCSyncPanel isSuperAdmin={isSuperAdmin} />
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  RefreshCw, CheckCircle, XCircle, Database,
  ArrowRight, AlertTriangle, Clock
} from 'lucide-react';

const PhishingIOCSyncPanel = ({ isSuperAdmin }) => {
  const { getToken } = useAuth();

  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError]     = useState(null);

  // ── Fetch status ────────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/phishing-feed/sync-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data);

      // Keep polling if running
      if (data.last_sync?.status === 'running') {
        setTimeout(() => fetchStatus(true), 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // ── Trigger sync ────────────────────────────────────────────────────────────

  const triggerSync = async (newOnly = false) => {
    setSyncing(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/phishing-feed/sync-to-iocs', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_only: newOnly })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTimeout(() => fetchStatus(true), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const formatTime = (ts) => {
    if (!ts) return '—';
    const diff = Math.floor((new Date() - new Date(ts)) / 1000);
    if (diff < 60)    return `${diff}s ago`;
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  const getStatusIcon = (s) => {
    if (s === 'success') return <CheckCircle className="w-3.5 h-3.5 text-green-400" />;
    if (s === 'running') return <RefreshCw className="w-3.5 h-3.5 text-blue-400 animate-spin" />;
    if (s === 'error')   return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    return <Clock className="w-3.5 h-3.5 text-slate-400" />;
  };

  // ── Coverage % ───────────────────────────────────────────────────────────────

  const coverage = status
    ? status.phishing_feed_total > 0
      ? Math.round((status.ioc_synced_count / status.phishing_feed_total) * 100)
      : 0
    : null;

  const isRunning = status?.last_sync?.status === 'running';
  const unsynced  = status ? status.phishing_feed_total - status.ioc_synced_count : 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-3 flex items-center gap-2 text-slate-400 text-xs">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        <span>Loading IOC sync status...</span>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
      <div className="px-3 py-2.5 flex items-center justify-between gap-3">

        {/* Left: label + stats */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5 text-blue-400" />
            <ArrowRight className="w-3 h-3 text-slate-500" />
            <Database className="w-3.5 h-3.5 text-cyan-400" />
          </div>

          <span className="text-slate-300 text-xs font-medium">IOC Sync</span>

          {status && (
            <>
              {/* Synced count */}
              <span className="bg-cyan-500/15 text-cyan-400 text-xs px-2 py-0.5 rounded-full font-mono">
                {status.ioc_synced_count.toLocaleString()} synced
              </span>

              {/* Coverage bar */}
              {coverage !== null && (
                <div className="flex items-center gap-1.5">
                  <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        coverage === 100 ? 'bg-green-500' :
                        coverage >= 80   ? 'bg-blue-500' :
                        coverage >= 50   ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${coverage}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400">{coverage}%</span>
                </div>
              )}

              {/* Unsynced warning */}
              {unsynced > 0 && (
                <span className="flex items-center gap-1 text-xs text-yellow-400/80">
                  <AlertTriangle className="w-3 h-3" />
                  {unsynced} unsynced
                </span>
              )}
            </>
          )}
        </div>

        {/* Right: last sync + buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {status?.last_sync && (
            <div className="flex items-center gap-1 text-xs text-slate-400">
              {getStatusIcon(status.last_sync.status)}
              <span>{isRunning ? 'Syncing...' : formatTime(status.last_sync.finished_at)}</span>
            </div>
          )}

          {!status?.last_sync && (
            <span className="text-xs text-slate-500">Never synced</span>
          )}

          {isSuperAdmin && (
            <div className="flex items-center gap-1.5">
              {/* Sync new only */}
              {unsynced > 0 && (
                <button
                  onClick={() => triggerSync(true)}
                  disabled={syncing || isRunning}
                  className="px-2 py-1 bg-slate-700/80 hover:bg-slate-600 border border-slate-600
                             text-slate-300 text-xs rounded transition-all
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sync only new entries"
                >
                  +New
                </button>
              )}

              {/* Full sync */}
              <button
                onClick={() => triggerSync(false)}
                disabled={syncing || isRunning}
                className="flex items-center gap-1.5 px-2.5 py-1
                           bg-blue-500/20 hover:bg-blue-500/30
                           border border-blue-500/30 hover:border-blue-500/50
                           text-blue-300 text-xs rounded-md transition-all
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3 h-3 ${(syncing || isRunning) ? 'animate-spin' : ''}`} />
                <span>{syncing || isRunning ? 'Syncing' : 'Sync All'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="px-3 pb-2 flex items-center gap-1.5 text-red-400 text-xs">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default PhishingIOCSyncPanel;